'use client';

import { c as _c } from "react/compiler-runtime";
import { type ComponentProps, useId } from 'react';
import { CheckIcon, MinusIcon } from '@phosphor-icons/react';
import { cva } from 'class-variance-authority';
import { Checkbox as CheckboxPrimitive } from 'radix-ui';
import { cn } from './lib/cn';
const checkboxVariants = cva(['group grid size-5 shrink-0 cursor-pointer place-content-center rounded border transition-colors', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-role-info focus-visible:ring-offset-2', 'disabled:cursor-not-allowed disabled:border-role-disabled disabled:bg-role-disabled'], {
  variants: {
    error: {
      false: ['border-role-divider bg-white', 'hover:bg-role-surface-container', 'data-[state=checked]:border-role-info data-[state=checked]:bg-role-info', 'data-[state=indeterminate]:border-role-info data-[state=indeterminate]:bg-role-info', 'disabled:data-[state=checked]:border-role-disabled disabled:data-[state=checked]:bg-role-disabled', 'disabled:data-[state=indeterminate]:border-role-disabled disabled:data-[state=indeterminate]:bg-role-disabled'],
      true: ['border-role-error bg-role-error-container', 'data-[state=checked]:border-role-error data-[state=checked]:bg-role-error', 'data-[state=indeterminate]:border-role-error data-[state=indeterminate]:bg-role-error']
    }
  },
  defaultVariants: {
    error: false
  }
});
export type CheckboxProps = Omit<ComponentProps<typeof CheckboxPrimitive.Root>, 'children'> & {
  error?: boolean;
  label?: string;
};
function Checkbox(t0) {
  const $ = _c(26);
  let className;
  let disabled;
  let id;
  let label;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      error: t1,
      label,
      id,
      disabled,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = disabled;
    $[3] = id;
    $[4] = label;
    $[5] = props;
    $[6] = t1;
  } else {
    className = $[1];
    disabled = $[2];
    id = $[3];
    label = $[4];
    props = $[5];
    t1 = $[6];
  }
  const error = t1 === undefined ? false : t1;
  const generatedId = useId();
  const checkboxId = id ?? generatedId;
  const t2 = error || undefined;
  let t3;
  if ($[7] !== className || $[8] !== error) {
    t3 = cn(checkboxVariants({
      error
    }), className);
    $[7] = className;
    $[8] = error;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <CheckboxPrimitive.Indicator className="grid place-content-center text-white disabled:text-role-on-disabled"><CheckIcon className="hidden size-3.5 group-data-[state=checked]:block" weight="bold" /><MinusIcon className="hidden size-3.5 group-data-[state=indeterminate]:block" weight="bold" /></CheckboxPrimitive.Indicator>;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== checkboxId || $[12] !== disabled || $[13] !== props || $[14] !== t2 || $[15] !== t3) {
    t5 = <CheckboxPrimitive.Root aria-invalid={t2} className={t3} disabled={disabled} id={checkboxId} {...props}>{t4}</CheckboxPrimitive.Root>;
    $[11] = checkboxId;
    $[12] = disabled;
    $[13] = props;
    $[14] = t2;
    $[15] = t3;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const checkboxElement = t5;
  if (!label) {
    return checkboxElement;
  }
  const t6 = disabled && "cursor-not-allowed text-role-outline";
  let t7;
  if ($[17] !== t6) {
    t7 = cn("cursor-pointer select-none text-role-on-background text-sm", t6);
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== checkboxId || $[20] !== label || $[21] !== t7) {
    t8 = <label className={t7} htmlFor={checkboxId}>{label}</label>;
    $[19] = checkboxId;
    $[20] = label;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== checkboxElement || $[24] !== t8) {
    t9 = <div className="flex items-center gap-2">{checkboxElement}{t8}</div>;
    $[23] = checkboxElement;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
}
export { Checkbox };